import React, { useMemo } from 'react';
import { Table as AntTable, Tag } from 'antd';

const headerStyle = { color: '#525252', whiteSpace: 'nowrap' };

const num = (v) => (v == null ? '-' : Number(v).toLocaleString('th-TH'));

// วินาที → อ่านง่าย (95 → 1:35 น.)
export const readableSeconds = (s) => {
  if (s == null) return '-';
  if (s < 60) return `${s} วิ`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m < 60) return `${m}:${String(rest).padStart(2, '0')} น.`;
  return `${Math.floor(m / 60)} ชม. ${m % 60} น.`;
};

// เข้มตามสัดส่วนของแถวนั้น ๆ — ตาราง 29 คอลัมน์ ถ้าไม่มีสีช่วยจะอ่านไม่ออกเลย
// ช่องที่ช้ากว่า 2 นาทีใช้โทนแดง จะได้สะดุดตาทันทีว่าใครมีเคสค้าง
const shade = (ratio, slow) => {
  if (!ratio) return undefined;
  const alpha = Math.min(0.85, 0.08 + ratio * 1.6).toFixed(3);
  return { backgroundColor: slow ? `rgba(255, 77, 79, ${alpha})` : `rgba(24, 144, 255, ${alpha})` };
};

const PickupTable = ({ data, loading, cumulative, showPercent }) => {
  const buckets = data?.buckets || [];
  const rows = data?.rows || [];
  const maxSeconds = data?.maxSeconds ?? 120;

  const dataSource = useMemo(
    () =>
      rows.map((row) => {
        let running = 0;
        const cells = row.counts.map((n) => {
          running += n;
          return cumulative ? running : n;
        });
        return { ...row, cells, key: row.userId };
      }),
    [rows, cumulative]
  );

  // แถวรวมทุกคน
  const totals = useMemo(() => {
    if (!rows.length) return null;
    const counts = new Array(buckets.length).fill(0);
    let total = 0;
    let busy = 0;
    let weighted = 0;
    rows.forEach((r) => {
      r.counts.forEach((n, i) => { counts[i] += n; });
      total += r.total;
      busy += r.busyCount;
      weighted += r.avgSeconds * r.total;
    });
    let running = 0;
    const cells = counts.map((n) => { running += n; return cumulative ? running : n; });
    return { total, busy, cells, avgSeconds: total ? Math.round(weighted / total) : 0 };
  }, [rows, buckets.length, cumulative]);

  const columns = [
    {
      title: 'พนักงาน',
      dataIndex: 'userName',
      key: 'userName',
      fixed: 'left',
      width: 130,
      onHeaderCell: () => ({ style: headerStyle }),
      render: (text) => <b>{text}</b>,
    },
    {
      title: 'เคสทั้งหมด',
      dataIndex: 'total',
      key: 'total',
      fixed: 'left',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.total - b.total,
      onHeaderCell: () => ({ style: headerStyle }),
      render: (v) => num(v),
    },
    {
      title: 'เฉลี่ย',
      dataIndex: 'avgSeconds',
      key: 'avgSeconds',
      width: 90,
      align: 'center',
      sorter: (a, b) => a.avgSeconds - b.avgSeconds,
      onHeaderCell: () => ({ style: headerStyle }),
      render: (v) => (
        <Tag color={v <= 30 ? 'green' : v <= 120 ? 'gold' : 'red'}>{readableSeconds(v)}</Tag>
      ),
    },
    {
      title: 'ติดเคสอื่น',
      dataIndex: 'busyCount',
      key: 'busyCount',
      width: 95,
      align: 'center',
      sorter: (a, b) => a.busyCount / (a.total || 1) - b.busyCount / (b.total || 1),
      onHeaderCell: () => ({ style: headerStyle }),
      render: (v, record) => (record.total ? `${((v / record.total) * 100).toFixed(0)}%` : '-'),
    },
    ...buckets.map((bucket, i) => {
      const slow = bucket.to === null || bucket.to > maxSeconds;
      return {
        title: cumulative ? bucket.cumLabel : bucket.label,
        key: `b${i}`,
        width: slow ? 108 : 78,
        align: 'center',
        onHeaderCell: () => ({ style: { ...headerStyle, color: slow ? '#cf1322' : headerStyle.color } }),
        render: (_, record) => {
          if (!record.total) return '-';
          const value = record.cells[i];
          const ratio = value / record.total;
          return (
            <div style={{ ...shade(ratio, slow), padding: '4px 0', borderRadius: 4 }}>
              {showPercent ? `${(ratio * 100).toFixed(1)}%` : num(value)}
            </div>
          );
        },
      };
    }),
    {
      title: 'ช้าสุด',
      dataIndex: 'maxSeconds',
      key: 'maxSeconds',
      width: 110,
      align: 'center',
      sorter: (a, b) => a.maxSeconds - b.maxSeconds,
      onHeaderCell: () => ({ style: headerStyle }),
      render: (v) => readableSeconds(v),
    },
  ];

  const FIXED_COLS = 4; // พนักงาน / เคสทั้งหมด / เฉลี่ย / ติดเคสอื่น

  return (
    <div className="overflow-x-auto">
      <AntTable
        loading={loading}
        className="border-1 rounded-lg border-gray-200"
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        rowKey="userId"
        size="small"
        scroll={{ x: 'max-content' }}
        summary={() =>
          totals ? (
            <AntTable.Summary fixed>
              <AntTable.Summary.Row style={{ background: '#fafafa', fontWeight: 700 }}>
                <AntTable.Summary.Cell index={0}>รวมทุกคน</AntTable.Summary.Cell>
                <AntTable.Summary.Cell index={1} align="center">{num(totals.total)}</AntTable.Summary.Cell>
                <AntTable.Summary.Cell index={2} align="center">{readableSeconds(totals.avgSeconds)}</AntTable.Summary.Cell>
                <AntTable.Summary.Cell index={3} align="center">
                  {totals.total ? `${((totals.busy / totals.total) * 100).toFixed(0)}%` : '-'}
                </AntTable.Summary.Cell>
                {totals.cells.map((v, i) => (
                  <AntTable.Summary.Cell key={i} index={FIXED_COLS + i} align="center">
                    {showPercent ? `${((v / totals.total) * 100).toFixed(1)}%` : num(v)}
                  </AntTable.Summary.Cell>
                ))}
                <AntTable.Summary.Cell index={FIXED_COLS + totals.cells.length} align="center">-</AntTable.Summary.Cell>
              </AntTable.Summary.Row>
            </AntTable.Summary>
          ) : null
        }
      />
    </div>
  );
};

export default PickupTable;
